import {inject, Injectable, resource, signal} from '@angular/core';
import {
    ConfigMatrixApi,
    Kpi,
    Portfolio,
    PortfolioApi,
    Segment,
    SegmentResponse,
    type SegmentStatistic
} from '../../api-client-kt/src';
import {PortfolioSegmentWorkbook} from './portfolio-segment-workbook';
import {KeyNamePair} from '../components/app-config-matrix/app-config-matrix.component';
import {StringUtils} from '../lib/strings';
import {AppConstants} from '../app.constants';
import {AccessControlService} from '../security/access-control.service';

export interface SegmentWithNames extends Segment {
    segmentName?: string;
    officeName?: string;
    lobName?: string;
    count: number;
    statsByStatusAndPeriod: SegmentStatistic[];
    kpis: Kpi[];
}

export interface SegmentWithPermissions extends SegmentWithNames {
    hasPermission: boolean;
    policyRule: string;
}


const appLobs: KeyNamePair[] = [
    { key: "AVN", name: "Aviation" }, { key: "CAS", name: "Casualty" }, { key: "CYB", name: "Cyber" },
    { key: "FLS", name: "Financial Lines" }, { key: "MAR", name: "Marine" }, { key: "PAR", name: "Parametric" },
    { key: "PRP", name: "Property" }
];

const appLobsLookup: { [key: string]: string } = {
    "ART": "Fine Arts"
};

const appOffices: KeyNamePair[] = [
    { key: "ASIA", name: "Asia" }, { key: "AUT", name: "Austria" }, { key: "CEE", name: "CEE" },
    { key: "ESP", name: "Iberia&LatAm" }, { key: "FRA", name: "France" }, { key: "GBR", name: "UK" },
    { key: "GER", name: "Germany" }, { key: "ITA", name: "Italy" }, { key: "N2G", name: "N2G" }
];

export interface RefdataService {
    officeKeys(): string[];
    lobKeys(): string[];
    lookupLobName(lobKey: string): string;
    lookupOfficeName(officeKey: string): string;
}


export interface ConfigMatrix<V> {
    [officeKey: string]: {
        [lobKey: string]: V;
    };
}

export interface ConfigMatrixStore {
    [matrixKey: string]: ConfigMatrix<any>;
}

/**
 * A service that provides and manages portfolio data, including portfolio segments, configuration matrices,
 * line-of-business (LOB) data, office data, and portfolio-related operations.
 * This service integrates with APIs to fetch portfolio data and applies access control rules to segments.
 *
 * @implements RefdataService
 */
@Injectable({
    providedIn: 'root'
})
export class PortfolioDataContext implements RefdataService {

    readonly lobs = appLobs;
    readonly offices = appOffices;

    readonly portfolioId = signal<string | undefined>(undefined);
    readonly portfolio = resource({
        params: () => this.portfolioId(),
        loader: ({ params }) => this.fetchPortfolio(params),
        defaultValue: PortfolioSegmentWorkbook.NonePortfolio
    });

    // the members of the portfolio as a Wijmo CollectionView
    readonly segments = resource({
         params: () => this.portfolioId(),
         loader: ({params}) => this.fetchPortfolioSegments(params)
     });


    readonly configMatrices = resource({
        params: () => this.portfolioId(),
        loader: ({ params }) => this.fetchConfigMatrices(params)
    })

    private readonly cfgMatrixApi = inject(ConfigMatrixApi);
    private readonly portfolioApi = inject(PortfolioApi);

    private readonly accessControlSvc = inject(AccessControlService);

    constructor() {
    }

    static isNullPortfolioId(portfolioId: string | undefined | null): portfolioId is undefined | null {
        return StringUtils.isNullOrEmpty(portfolioId) || portfolioId === AppConstants.NULL_UUID;
    }

    officeKeys(): string[] {
        return this.offices.map(office => office.key);
    }

    lobKeys(): string[] {
        return this.lobs.map(lob => lob.key);
    }

    lookupLobName(lobKey: string): string {
        return this.lobs.find(lob => lob.key === lobKey)?.name ?? appLobsLookup[lobKey] ?? '';
    }

    lookupOfficeName(officeKey: string): string {
        return this.offices.find(office => office.key === officeKey)?.name ?? '';
    }

    async activatePortfolioById(portfolioId: string) {
        console.log('Activating portfolio', portfolioId);
        const currentPortfolioId = this.portfolioId();

        if (PortfolioDataContext.isNullPortfolioId(currentPortfolioId)) {
            this.portfolioId.set(portfolioId);
        } else if (currentPortfolioId !== portfolioId) {
            console.error(`Cannot activate portfolio ${portfolioId}, current portfolio is ${currentPortfolioId}`);
        }
    }

    async deactivatePortfolio() {
        this.configMatrices.set(undefined);
        this.portfolioId.set(undefined);
        this.portfolio.set(PortfolioSegmentWorkbook.NonePortfolio);
    }

    private async fetchPortfolio(portfolioId: string | undefined): Promise<Portfolio> {
        if (PortfolioDataContext.isNullPortfolioId(portfolioId)) return PortfolioSegmentWorkbook.NonePortfolio;
        return await this.portfolioApi.getPortfolio({ id: portfolioId });
    }

    async fetchPortfolioSegments(portfolioId: string | undefined): Promise<SegmentWithPermissions[]> {
        if (PortfolioDataContext.isNullPortfolioId(portfolioId)) {
            return [];
        } else {
            const segments  = await this.portfolioApi.getSegments({ id: portfolioId });
            const segmentsWithNames = segments.map(segment => this.toSegmentWithName(segment));
            this.applySegmentAccessRules(segmentsWithNames);
            const allowedSegmentsWithNames = (segmentsWithNames as SegmentWithPermissions[]).filter(s => s.hasPermission);
            return allowedSegmentsWithNames;
        }
    }

    toSegmentWithName(segmentResponse: SegmentResponse): SegmentWithNames {
        const segment = segmentResponse.segment;
        return {
            id: segment.id,
            segmentName: `${this.lookupLobName(segment.lobKey)} - ${this.lookupOfficeName(segment.officeKey)}`,
            officeKey: segment.officeKey,
            lobKey: segment.lobKey,
            portfolioId: segment.portfolioId,
            status: segment.status,
            createdBy: segment.createdBy,
            createdAt: segment.createdAt,
            updatedBy: segment.updatedBy,
            updatedAt: segment.updatedAt,
            officeName: this.lookupOfficeName(segment.officeKey),
            lobName: this.lookupLobName(segment.lobKey),
            count: segmentResponse.accountCount,
            statsByStatusAndPeriod: segmentResponse.statsByStatusAndPeriod
        } as SegmentWithNames;
    }

    applySegmentAccessRules(segments: SegmentWithNames[] | undefined) {
        segments?.forEach(m => {
            const segmentAccess = this.accessControlSvc.canAccessSegment(m);
            const segment = m as SegmentWithPermissions;
            segment.hasPermission = segmentAccess.allowed;
            segment.policyRule = segmentAccess.reason ?? '-/-';
        });
    }

    private async fetchConfigMatrices(portfolioId: string | undefined): Promise<ConfigMatrixStore | undefined> {
        if (PortfolioDataContext.isNullPortfolioId(portfolioId)) return undefined;

        const matrices = await this.cfgMatrixApi.getConfigMatrices({ id: portfolioId });
        const matrixValuesRequests = matrices.map(m => this.cfgMatrixApi.getConfigMatrixValues({ id: portfolioId, key: m.key }));
        const matrixValues = await Promise.all(matrixValuesRequests);

        function getOrExtend(o: any, k: string) {
            if (!o.hasOwnProperty(k)) o[k] = {};
            return o[k];
        }

        const store: ConfigMatrixStore = {};

        matrices.forEach((m, i) => {
            const mKey = m.key;
            const mx = getOrExtend(store, mKey);

            const mVal = matrixValues[i];
            mVal.forEach(v => getOrExtend(mx, v.officeKey!)[v.lobKey!] = v.value);
        });

        return store;
    }
}
